import type { ClientAnswer } from '@/api/orderDetails'
import { buildGoogleMapsHref } from '@/components/shared/LocationLink'

const NA_VALUES = new Set(['', 'n/a', 'na', 'n.a.', 'none', '-', '–', '—'])

export function isNaLike(value: string): boolean {
  const v = value?.trim().toLowerCase()
  return !v || NA_VALUES.has(v)
}

export interface SurveyLocationDisplay {
  label: string
  href: string
  /** Raw coordinates when the answer is coordinate-based, for reverse geocoding. */
  coords: { lat: number; lng: number } | null
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value == null || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

export function parseCoordinates(value: string | unknown): { lat: number; lng: number } | null {
  if (value == null) return null
  const str = typeof value === 'string' ? value : JSON.stringify(value)
  try {
    const parsed = JSON.parse(str) as { lat?: number; lng?: number }
    if (typeof parsed?.lat === 'number' && typeof parsed?.lng === 'number') {
      return { lat: parsed.lat, lng: parsed.lng }
    }
  } catch {
    // continue
  }
  const normalized = str.replace(/'/g, '"')
  try {
    const parsed = JSON.parse(normalized) as { lat?: number; lng?: number }
    if (typeof parsed?.lat === 'number' && typeof parsed?.lng === 'number') {
      return { lat: parsed.lat, lng: parsed.lng }
    }
  } catch {
    // continue
  }
  const match =
    str.match(/lat['"]?\s*:\s*([-\d.]+).*?lng['"]?\s*:\s*([-\d.]+)/i) ||
    str.match(/latitude['"]?\s*:\s*([-\d.]+).*?longitude['"]?\s*:\s*([-\d.]+)/i)
  if (match) {
    const lat = parseFloat(match[1])
    const lng = parseFloat(match[2])
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng }
  }
  return null
}

function extractBaseQuestionCode(code: string): string {
  const trimmed = code.trim()
  const withoutIndex = trimmed.replace(/_(\d+)$/, '')
  const parts = withoutIndex.split('_')
  if (parts.length >= 2 && /^[a-z]{2,4}\d*$/i.test(parts[0])) {
    return parts[0].toUpperCase()
  }
  return withoutIndex
}

export function getPickupDropoffGoogleMapsUrl(answers: ClientAnswer[]): string | null {
  let pickup: { lat: number; lng: number } | null = null
  let dropoff: { lat: number; lng: number } | null = null
  for (const a of answers) {
    const base = extractBaseQuestionCode(a.questionCode)
    if (base !== 'LOC3' && base !== 'LOC4') continue
    const rawStr = typeof a.rawAnswer === 'string' ? a.rawAnswer : a.answer
    const coords = parseCoordinates(rawStr) ?? parseCoordinates(a.rawAnswer)
    if (!coords) continue
    if (base === 'LOC3') pickup = coords
    if (base === 'LOC4') dropoff = coords
  }
  if (!pickup || !dropoff) return null
  return `https://www.google.com/maps/dir/${pickup.lat},${pickup.lng}/${dropoff.lat},${dropoff.lng}`
}

interface SurveyFileValue {
  name?: string
  type?: string
  mimeType?: string
  url?: string
  file_url?: string
  uploadResult?: {
    file_url?: string
    original_filename?: string
  }
}

function parseFileUrl(value: unknown): { url: string; name?: string; isImage: boolean } | null {
  if (value == null) return null
  let obj: SurveyFileValue
  if (typeof value === 'object' && value !== null) {
    obj = value as SurveyFileValue
  } else if (typeof value === 'string') {
    try {
      obj = JSON.parse(value) as SurveyFileValue
    } catch {
      return null
    }
  } else {
    return null
  }
  const url = obj.url ?? obj.uploadResult?.file_url ?? obj.file_url
  if (!url || typeof url !== 'string') return null
  const isImage =
    obj.type === 'image' ||
    (typeof obj.mimeType === 'string' && obj.mimeType.startsWith('image/'))
  return {
    url,
    name: obj.uploadResult?.original_filename ?? obj.name,
    isImage,
  }
}

export function readLocationLabelFromObject(obj: Record<string, unknown>): string | null {
  const candidates = [
    obj.formatted_address,
    obj.formattedAddress,
    obj.address,
    obj.label,
    obj.name,
    obj.text,
    obj.description,
    obj.place_name,
    obj.placeName,
  ]
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim()
  }
  return null
}

function readLocationHrefFromObject(obj: Record<string, unknown>): string | null {
  const candidates = [
    obj.google_maps_link,
    obj.googleMapsLink,
    obj.google_maps_url,
    obj.maps_url,
    obj.url,
  ]
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim()
  }
  return null
}

function parseLocationObject(value: unknown): SurveyLocationDisplay | null {
  const obj = asRecord(value)
  if (!obj) return null

  const label = readLocationLabelFromObject(obj)
  const explicitHref = readLocationHrefFromObject(obj)
  const coords =
    parseCoordinates(value) ??
    (typeof obj.lat === 'number' && typeof obj.lng === 'number'
      ? { lat: obj.lat, lng: obj.lng }
      : null) ??
    (() => {
      const nested = asRecord(obj.coordinates)
      if (nested && typeof nested.lat === 'number' && typeof nested.lng === 'number') {
        return { lat: nested.lat, lng: nested.lng }
      }
      return null
    })()

  if (coords) {
    const href = explicitHref ?? `https://www.google.com/maps?q=${coords.lat},${coords.lng}`
    const displayLabel = label ?? `${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}`
    return { label: displayLabel, href, coords }
  }

  if (label) {
    return { label, href: buildGoogleMapsHref(label, explicitHref), coords: null }
  }

  return null
}

function isLocationQuestionType(questionType: string | undefined, questionCode: string | undefined): boolean {
  const type = questionType?.trim().toLowerCase()
  if (type === 'location' || type === 'address' || type === 'geo' || type === 'gps_auto') {
    return true
  }
  const base = extractBaseQuestionCode(questionCode ?? '')
  return /^LOC\d*$/i.test(base) || base.startsWith('LOC')
}

/**
 * Location / appointment-address survey answers as a clickable maps link.
 */
export function getSurveyLocationDisplay(
  answer: string,
  rawAnswer: unknown,
  questionType: string | undefined,
  questionCode: string | undefined,
  locale: 'en' | 'ar'
): SurveyLocationDisplay | null {
  const fromObject = parseLocationObject(rawAnswer) ?? parseLocationObject(answer)
  if (fromObject) return fromObject

  const coords =
    parseCoordinates(typeof rawAnswer === 'string' ? rawAnswer : answer) ??
    parseCoordinates(rawAnswer) ??
    parseCoordinates(answer)
  if (coords) {
    return {
      label: `${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}`,
      href: `https://www.google.com/maps?q=${coords.lat},${coords.lng}`,
      coords,
    }
  }

  const trimmedAnswer = answer?.trim() ?? ''
  if (
    isLocationQuestionType(questionType, questionCode) &&
    trimmedAnswer &&
    !isNaLike(trimmedAnswer) &&
    !/^-?\d+\.?\d*\s*,\s*-?\d+\.?\d*$/.test(trimmedAnswer)
  ) {
    return {
      label: trimmedAnswer,
      href: buildGoogleMapsHref(trimmedAnswer),
      coords: null,
    }
  }

  if (
    isLocationQuestionType(questionType, questionCode) &&
    coords === null &&
    trimmedAnswer &&
    /^https?:\/\//i.test(trimmedAnswer)
  ) {
    return {
      label: locale === 'ar' ? 'فتح في خرائط جوجل' : 'Open in Google Maps',
      href: trimmedAnswer,
      coords: null,
    }
  }

  return null
}

export function getSurveyImageFileInfo(
  answer: string,
  rawAnswer: unknown
): { url: string; name?: string } | null {
  const info = parseFileUrl(rawAnswer) ?? parseFileUrl(answer)
  if (!info || !info.isImage) return null
  return { url: info.url, name: info.name }
}

export function formatSurveyAnswerText(
  answer: string,
  rawAnswer: unknown,
  questionType: string | undefined,
  locale: 'en' | 'ar'
): string {
  const fileInfo = parseFileUrl(rawAnswer) ?? parseFileUrl(answer)
  if (fileInfo && !fileInfo.isImage) {
    return fileInfo.name || fileInfo.url
  }

  if (isNaLike(answer)) {
    return locale === 'ar' ? 'غير متوفر' : 'N/A'
  }

  const lower = answer?.trim().toLowerCase()
  if (questionType?.toLowerCase() === 'boolean' || lower === 'true' || lower === 'false') {
    if (lower === 'true' || lower === 'yes') return locale === 'ar' ? 'نعم' : 'Yes'
    if (lower === 'false' || lower === 'no') return locale === 'ar' ? 'لا' : 'No'
  }

  return answer
}

export function serviceHasSurveyContent(service: {
  clientAnswersWithQuestions?: ClientAnswer[]
  pendingSurveys?: unknown[]
  clientSurveyResponses?: unknown[]
  hasPendingSurvey?: boolean
}): boolean {
  return (
    (service.clientAnswersWithQuestions?.length ?? 0) > 0 ||
    (service.pendingSurveys?.length ?? 0) > 0 ||
    (service.clientSurveyResponses?.length ?? 0) > 0 ||
    Boolean(service.hasPendingSurvey)
  )
}
